import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { useCreatePricingFeedback } from "@/hooks/useApi";
import {
  Heart,
  ThumbsUp,
  Minus,
  DollarSign,
  X,
  MessageCircle,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import type {
  CreatePricingFeedbackRequest,
  PricingOption,
} from "@/lib/campaignsApi";

interface PricingFeedbackFormProps {
  campaignId: string;
  pricingOptions: PricingOption[];
  onSuccess?: () => void;
  className?: string;
}

const sentimentOptions = [
  {
    value: "LOVE" as const,
    label: "Love it!",
    emoji: "😍",
    icon: Heart,
    color: "text-red-500",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  {
    value: "LIKE" as const,
    label: "Like it",
    emoji: "🙂",
    icon: ThumbsUp,
    color: "text-green-500",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
  },
  {
    value: "NEUTRAL" as const,
    label: "Neutral",
    emoji: "😐",
    icon: Minus,
    color: "text-gray-500",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
  },
  {
    value: "EXPENSIVE" as const,
    label: "Too expensive",
    emoji: "😕",
    icon: DollarSign,
    color: "text-yellow-500",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
  },
  {
    value: "NOT_INTERESTED" as const,
    label: "Not interested",
    emoji: "👎",
    icon: X,
    color: "text-red-400",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
];

const PricingFeedbackForm: React.FC<PricingFeedbackFormProps> = ({
  campaignId,
  pricingOptions,
  onSuccess,
  className,
}) => {
  const [selectedSentiment, setSelectedSentiment] = useState<
    CreatePricingFeedbackRequest["sentiment"] | null
  >(null);
  const [selectedPricingOption, setSelectedPricingOption] = useState<
    string | null
  >(null);
  const [willingnessToPay, setWillingnessToPay] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);

  const createPricingFeedbackMutation = useCreatePricingFeedback();

  const handleSubmit = async () => {
    if (!selectedSentiment) {
      toast({
        title: "Please select your sentiment",
        description: "Choose how you feel about the pricing options.",
        variant: "destructive",
      });
      return;
    }

    try {
      const feedbackData: CreatePricingFeedbackRequest = {
        sentiment: selectedSentiment,
        willingnessToPay: willingnessToPay > 0 ? willingnessToPay : undefined,
        selectedPricingOptionId: selectedPricingOption || undefined,
        comment: comment.trim() || undefined,
      };

      await createPricingFeedbackMutation.mutateAsync({
        campaignId,
        data: feedbackData,
      });

      toast({
        title: "Thanks for your pricing feedback! 🎉",
        description: "Your insights help creators set the right price point.",
      });

      // Reset form
      setSelectedSentiment(null);
      setSelectedPricingOption(null);
      setWillingnessToPay(0);
      setComment("");
      setShowComment(false);

      onSuccess?.();
    } catch (error) {
      toast({
        title: "Error submitting feedback",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const getMinMaxPrices = () => {
    if (pricingOptions.length === 0) return { min: 0, max: 100 };

    const prices = pricingOptions.map((opt) => parseFloat(opt.price) || 0);
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  };

  const { min, max } = getMinMaxPrices();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Sentiment Selection */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-blue-600" />
            How do you feel about these pricing options?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {sentimentOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedSentiment === option.value;

              return (
                <motion.button
                  key={option.value}
                  onClick={() => setSelectedSentiment(option.value)}
                  className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? `${option.bgColor} ${option.borderColor} border-2 shadow-lg scale-105`
                      : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="text-center space-y-2">
                    <div className="text-3xl">{option.emoji}</div>
                    <Icon className={`w-5 h-5 mx-auto ${option.color}`} />
                    <div className="text-sm font-medium text-gray-700">
                      {option.label}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pricing Options Selection */}
      {pricingOptions.length > 0 && (
        <Card className="border-0 shadow-lg bg-gradient-to-r from-emerald-50 to-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Which pricing tier appeals to you most?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pricingOptions.map((option, index) => (
                <motion.button
                  key={option.id}
                  onClick={() => setSelectedPricingOption(option.id || null)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    selectedPricingOption === option.id
                      ? "border-emerald-500 bg-emerald-50 shadow-lg"
                      : "border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-gray-900">
                      Tier {index + 1}
                    </h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-600">
                        ${option.price}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {option.description}
                  </div>
                  {selectedPricingOption === option.id && (
                    <div className="mt-3 flex items-center text-emerald-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">Selected</span>
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Willingness to Pay Slider */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <DollarSign className="w-5 h-5 text-purple-600" />
            What would you be willing to pay for this product?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                ${willingnessToPay}
              </div>
              <div className="text-sm text-gray-600">
                {willingnessToPay === 0
                  ? "Select your price point"
                  : willingnessToPay <= min
                  ? "That's below the current options"
                  : willingnessToPay >= max
                  ? "You'd pay more than the highest tier!"
                  : "That's a reasonable price point"}
              </div>
            </div>

            <div className="relative">
              <input
                type="range"
                min={0}
                max={Math.max(max * 1.5, 100)}
                step={5}
                value={willingnessToPay}
                onChange={(e) => setWillingnessToPay(parseInt(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>$0</span>
                <span>${Math.max(max * 1.5, 100)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Optional Comment */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <MessageCircle className="w-5 h-5 text-amber-600" />
            Additional thoughts (optional)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!showComment ? (
            <Button
              variant="outline"
              onClick={() => setShowComment(true)}
              className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Add a comment about pricing
            </Button>
          ) : (
            <div className="space-y-3">
              <Textarea
                placeholder="Share your thoughts about the pricing, what you'd expect for this price, or any concerns..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="min-h-24 border-amber-200 focus:border-amber-400"
                maxLength={500}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {comment.length}/500 characters
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowComment(false);
                    setComment("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Remove comment
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="text-center">
        <Button
          onClick={handleSubmit}
          disabled={
            !selectedSentiment || createPricingFeedbackMutation.isPending
          }
          size="lg"
          className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 px-12 py-6 text-lg shadow-lg"
        >
          {createPricingFeedbackMutation.isPending ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              Submitting...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Submit Pricing Feedback
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PricingFeedbackForm;
